import React, { useEffect, useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import { CalendarClock, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

function formatEventDate(value, locale) {
  if (!value) return null;
  try {
    return format(parseISO(value.replace('Z', '+00:00')), "d 'de' MMMM, HH:mm", { locale });
  } catch {
    return null;
  }
}

// Card "Seguimientos activos" en la ficha de lead — solo lectura, GET
// /leads/{id}/events filtrado a FOLLOW_UP_SET (backend). Alcance reducido
// 2026-07-09 (docs/adam-intelligence-spec.md): sin canal, sin creación
// desde aquí — la única vía para generar estos eventos sigue siendo el
// botón "Marcar seguimiento" ya existente en LeadDetail.js.
export function ActiveFollowUpsCard({ leadId, refreshKey }) {
  const { api, language } = useAuth();
  const { t } = useTranslation();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}/events`);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error('Failed to fetch lead events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [api, leadId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, refreshKey]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-amber-400" />
        <h2 className="text-sm font-semibold text-zinc-200">
          {t('aiLeadScanner.activeFollowUps.title')}
        </h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-zinc-500">{t('aiLeadScanner.activeFollowUps.empty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {events.map((event) => (
            <div key={event.id} className="border-l-2 border-amber-500/40 pl-3 py-0.5">
              <p className="text-sm text-zinc-200">
                {formatEventDate(event.next_follow_up_at, locale) || formatEventDate(event.created_at, locale)}
              </p>
              {event.note && (
                <p className="text-sm text-zinc-400 mt-0.5">{event.note}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
